import { useState } from "react";
import { Plus, Inbox as InboxIcon, FolderPlus } from "lucide-react";
import clsx from "clsx";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import type { Accent, Card } from "../lib/types";
import { CardItem } from "./CardItem";

type Props = {
  /** Used as the fallback accent for the strip on loose cards (since they
   *  don't belong to a lane and may not have a per-card accent set). */
  threadAccent: Accent;
  cards: Card[];
  onAddCard: (title: string) => void;
  onCardClick: (card: Card) => void;
  /** Promote the entire inbox into a real lane: creates the lane, moves all
   *  current loose cards into it (preserving order), then the inbox empties
   *  and hides itself. */
  onPromote: (name: string) => Promise<void>;
};

/**
 * Inbox column for loose cards (cards with lane_id = null). Rendered in
 * lanes view ONLY when there's at least one loose card OR when the user
 * is mid-capture — so an empty inbox doesn't permanently occupy space.
 * Visually distinct from real lanes (dashed border, muted tint, smaller
 * header, narrower) to read as "transient holding pen" not "category".
 */
export function InboxColumn({
  threadAccent,
  cards,
  onAddCard,
  onCardClick,
  onPromote,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [promoteName, setPromoteName] = useState("");

  // The entire column is the droppable — landing anywhere on it (header,
  // empty space, between cards) sets lane_id = null on the dropped card.
  // Dropping precisely on a card still wins via closestCenter and uses the
  // card-typed drop path, which is what we want for reordering.
  const { setNodeRef, isOver } = useDroppable({
    id: "inbox-drop",
    data: { type: "inbox" },
  });

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "flex h-fit w-60 shrink-0 flex-col rounded-lane border border-dashed transition-colors",
        isOver
          ? "border-lavender-200 bg-lavender-50/40"
          : "border-border-strong/50 bg-canvas/30"
      )}
    >
      <div className="flex items-center gap-2 px-3.5 py-3">
        <InboxIcon size={12} className="text-ink-mute" />
        {promoting ? (
          <input
            autoFocus
            value={promoteName}
            onChange={(e) => setPromoteName(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === "Enter" && promoteName.trim()) {
                const name = promoteName.trim();
                setPromoting(false);
                setPromoteName("");
                await onPromote(name);
              } else if (e.key === "Escape") {
                setPromoting(false);
                setPromoteName("");
              }
            }}
            onBlur={() => {
              setPromoting(false);
              setPromoteName("");
            }}
            placeholder="name this lane…"
            className="input-bare flex-1 text-[12px] font-medium tracking-tight"
          />
        ) : (
          <>
            <span className="flex-1 text-[12px] font-medium tracking-tight text-ink-soft">
              inbox
            </span>
            {cards.length > 0 && (
              <button
                onClick={() => setPromoting(true)}
                title="save inbox as a lane"
                className="rounded p-0.5 text-ink-mute transition-colors hover:bg-surface/70 hover:text-ink"
              >
                <FolderPlus size={12} />
              </button>
            )}
            <span className="font-mono text-[10px] text-ink-mute">
              {cards.length || ""}
            </span>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2 px-2 pb-2">
        <SortableContext
          items={cards.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {cards.map((c) => (
            <CardItem
              key={c.id}
              card={c}
              fallbackAccent={threadAccent}
              onClick={() => onCardClick(c)}
            />
          ))}
        </SortableContext>

        {adding ? (
          <div className="rounded-card border border-border bg-surface p-2 shadow-card">
            <input
              autoFocus
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newTitle.trim()) {
                  onAddCard(newTitle.trim());
                  setNewTitle("");
                } else if (e.key === "Escape") {
                  setAdding(false);
                  setNewTitle("");
                }
              }}
              onBlur={() => {
                if (newTitle.trim()) {
                  onAddCard(newTitle.trim());
                  setNewTitle("");
                }
                setAdding(false);
              }}
              placeholder="quick thought..."
              className="input-bare w-full px-1.5 py-1 text-sm"
            />
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center justify-center gap-1.5 rounded-card px-3 py-2 text-xs text-ink-mute transition-colors hover:bg-surface/60 hover:text-ink-soft"
          >
            <Plus size={12} /> add to inbox
          </button>
        )}
      </div>
    </div>
  );
}
